import React, { useMemo } from 'react';
import { Alert, Box, Stack, Typography, Button, Divider, Tooltip } from '@mui/material';
import RefreshButton from '../common/RefreshButton';
import KpiRow, { KpiCell } from '../common/KpiRow';
import StatTile from '../common/StatTile';
import CollapsibleSection from '../common/CollapsibleSection';
import FulfillmentCostKpiCard from './FulfillmentCostKpiCard';
import { formatInt, formatMoney, formatPercent } from '../../utils/format';
import { formatMonthLabel } from '../../utils/formatters';
import { SP } from '../../theme/tokens';

// 🔴 Hausform statt `Intl.NumberFormat('en-GB')` (Regel-16-Gate B1, 2026-10-06).
// `formatMoney` liefert fuer `null`/`undefined` den Gedankenstrich — dieselbe
// Zusage, die `UNKNOWN` unten fuer die uebrigen Kacheln gibt. `digits: 0` ist
// die bewusst kompakte Kachel-Form (`6.651 €`), die diese Flaeche schon hatte.
const eur = (value) => formatMoney(value, { digits: 0 });
// 🔴 UNBEKANNT IST NICHT NULL. `Number(value || 0)` machte aus `undefined`
// eine 0 — und die Flaeche zeigte "€0 · 0 · 0" als waeren es Daten. Der Owner
// hat genau das live erwischt: zwei Streifen mit Null, ein dritter korrekt,
// und dieselbe Sitzung auf Mobile mit 77.097,29 €.
//
// `null`/`undefined` heissen "unbekannt" und rendern als Gedankenstrich.
// Eine echte 0 bleibt eine 0 — der Unterschied ist der ganze Punkt.
const UNKNOWN = '—';
// 🔴 Hier stand ein EIGENES `formatNumber` mit `toLocaleString('en-GB')` — eine
// englische Zahlform (`1,234.5`) in einer deutschen Oberfläche, die der Wächter
// nicht sieht (sie trägt ein Locale-Literal). Seit PR 2 kommt die Zahl aus
// `format.js` wie überall sonst.
const pct = (value, digits = 1) =>
  value == null ? UNKNOWN : formatPercent(Number(value) / 100, { digits });

/**
 * 🔴 W-08, nachgezogen. Der `catch` in `FulfillmentDashboard.refreshAllData`
 * und ein Fehlerhinweis waren da — aber die ZAHLEN darunter rendeten weiter
 * `€0`, `0`, `0.0%`, waehrend der Hinweis "die Werte unten sind unbekannt,
 * nicht null" behauptete. Genau der Widerspruch, den W-08 schliessen sollte:
 * eine Flaeche, die einen Ausfall als Null ausgibt.
 *
 * `StatTile` rendert `value ?? '—'`. Eine gefallene Quelle wird deshalb als
 * `null` uebergeben, nicht als 0 — und `undefined` schaltet die Zeile ab, die
 * ohne Daten ohnehin nichts aussagen kann. Eine echte 0 bleibt eine 0.
 */
const unknown = (failed, rendered) => (failed ? null : rendered);
const hintUnknown = (failed, text) => (failed ? undefined : text);

export default function FulfillmentKPIDashboard({
  overallError,
  ordersError,
  kpiOverall,
  overallAllKinds,
  kpiFiltered,
  rollingKpis,
  rollingError,
  rollingLoading,
  // 🔴 Erstes Bild. Ohne diesen Schalter rendert der Streifen "€0 · 0 · 0",
  // weil `kpiOverall` mit Nullen startet — Konzept §4.2 ("Loading") und F6.
  // `StatTile loading` + die schon gesetzten `reserveHint`/`reserveSeries`
  // halten die Kachelhoehe, statt sie beim Landen der Daten springen zu lassen.
  kpiLoading = false,
  isRefreshing,
  onRefresh,
}) {
  const totalCost = overallAllKinds
    ? Number(kpiOverall?.totals?.allKinds || 0)
    : Number(kpiOverall?.totals?.shipments || 0);

  const topCarriers = useMemo(
    () =>
      Object.entries(kpiOverall?.perCarrier || {})
        .sort((a, b) => (b[1] || 0) - (a[1] || 0))
        .slice(0, 3),
    [kpiOverall?.perCarrier]
  );

  // 🔴 W-41, dritter Fund — und der betrifft ALLE sechs Sparklines dieser
  // Flaeche, nicht nur die zwei falsch verdrahteten.
  //
  // `x` ging hier durch `formatMonthLabel` und wurde damit zu "Apr 26".
  // `Sparkline` versucht den Wert als Datum zu lesen, und `new Date('Apr 26')`
  // ist in V8 der 26. APRIL 2001 — der Ziffernteil wird als TAG gelesen, nicht
  // als Jahr. Der Hover zeigte deshalb "Apr 2001 | Mai 2001 | … | Sep 2001"
  // ueber Daten aus 2026. Gemessen, nicht vermutet:
  //
  //   x = "Apr 26"   -> Hover-Label "Apr 2001"   (falsches Jahr)
  //   x = "2026-04"  -> Hover-Label "Apr 2026"   (richtig)
  //
  // Dieselben sechs Punkte, dieselben Werte — nur das Label war falsch.
  // Die ROHE Monatsangabe geht an die Sparkline; lesbar gemacht wird sie erst
  // dort, wo ein Mensch sie liest (`monthSuffix`).
  const mapSeries = (arr = []) =>
    arr.map((p) => ({
      x: p.x || p.month || '',
      y: p.y || 0,
    }));

  // Aggregate source (`/orders/aggregate`) — W-08: `overallError`.
  const sparkAll = mapSeries(kpiOverall?.sparklineTotalAllKinds);
  const sparkShipments = mapSeries(kpiOverall?.sparklineTotalShipments);
  const sparkReturnRate = mapSeries(kpiOverall?.sparklineReturnRate);
  // Selection source (client-side reduce over `/orders`) — W-08: `ordersError`.
  const filteredSparkTotal = mapSeries(kpiFiltered?.sparklineTotal);
  const filteredSparkWarehouse = mapSeries(kpiFiltered?.sparklineWarehouse);
  const filteredSparkTransport = mapSeries(kpiFiltered?.sparklineTransport);

  // 🔴 W-41: der letzte Punkt ist der Wert der Kachel, und der Monat im Label
  // kommt aus DEMSELBEN Punkt. Vorher hiess die Kachel "· last 6 months" und
  // zeigte einen einzelnen Monat — eine Falschaussage, keine Rundung.
  const lastPoint = (arr) => (arr.length ? arr[arr.length - 1] : null);
  const lastAllKinds = lastPoint(sparkAll);
  const lastShipments = lastPoint(sparkShipments);
  const lastReturnRate = lastPoint(sparkReturnRate);

  const monthSuffix = (point) => {
    if (!point?.x) return '';
    const label = formatMonthLabel(point.x);
    return label && label !== UNKNOWN ? ` · ${label}` : '';
  };
  const moneyAt = (point) => (point ? eur(point.y) : null);
  const pctAt = (point) => (point ? pct(point.y) : null);

  // Drei Zustaende, drei Aussagen: laedt / Abruf gescheitert / wirklich leer.
  // "No carrier data." beim ersten Bild waere eine Behauptung ueber Daten, die
  // noch niemand gesehen hat.
  const topCarriersNote = kpiLoading
    ? 'Loading…'
    : overallError
      ? 'Unavailable.'
      : 'No carrier data.';

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: SP.card }}>
        <Typography variant="h6">Fulfillment KPIs</Typography>
        {/* E8 (2026-10-07): der eine Auffrisch-Knopf. Vorher eine beschriftete
            Schaltflaeche mit wechselndem Text — der Spinner sitzt jetzt im
            Symbol, und `disabled` kommt aus demselben `loading`.
            🔴 `firstLoad` (R16 W1): `isRefreshing` ist beim Montieren `true`
            (`FulfillmentDashboard.jsx:453-456` ruft `refreshAllData()`, dessen
            erste Zeile `setIsRefreshing(true)` ist), und darunter stehen
            Skelette. Ohne diesen Schalter drehte sich der Knopf im Aufbau neben
            den Skeletten mit — Anti-Pattern 11.
            `kpiLoading` ist genau die Marke „noch nie geladen": sie startet auf
            `true` und wird EINMAL auf `false` gesetzt, am Ende des ersten Abrufs
            (`FulfillmentDashboard.jsx:70`, `:408`); jeder spaetere Neulauf laesst
            sie stehen. Deshalb ist sie hier der richtige Diskriminator und nicht
            `kpiOverall`, das mit Nullen startet statt mit `null`. */}
        <RefreshButton
          ariaLabel="Refresh fulfillment KPIs"
          loading={isRefreshing}
          firstLoad={kpiLoading}
          onClick={onRefresh}
        />
      </Stack>

      {overallError && (
        // 🔴 Der Abruf ist fehlgeschlagen — das ist NICHT dasselbe wie "die
        // Zahlen sind null". Der Hinweis steht ueber dem Streifen (Konzept
        // §4.2, "Error"), die Kacheln darunter rendern `—` (siehe `unknown`),
        // und der Weg heraus ist ein Klick statt eines Neuladens der Seite.
        //
        // 🔴 Englisch, wie die ganze Flaeche: diese Datei und ihre Geschwister
        // ("Fulfillment KPIs", "Overall cost", "Refresh") sind durchgehend
        // englisch. Hier standen "Erneut versuchen" und ein deutscher Satz
        // (Gates zu PR 4, W2/W-4) — zwei Sprachen in EINEM Bauteil.
        <Alert
          severity="error"
          sx={{ mb: SP.card }}
          action={
            <Button color="inherit" size="small" onClick={onRefresh} disabled={isRefreshing}>
              Try again
            </Button>
          }
        >
          The KPIs could not be loaded. The values below are unknown, not zero.
        </Alert>
      )}

      {/*
        🔴 W-5 derselben Gates: die ZWEITE Quelle hat ihren eigenen Fehlertopf
        (`ordersError`, clientseitig aus `/orders`), aber keine Flaeche, die ihn
        erklaert. Der Nutzer sah in "Cost breakdown" ein `—` und konnte nicht
        unterscheiden, ob die Auswahl-Quelle ausgefallen ist oder schlicht
        nichts enthaelt — genau die Verwechslung, gegen die dieser Streifen
        sonst antritt. Der Hinweis steht ueber der zugeklappten Ebene, damit er
        auch ohne Aufklappen sichtbar ist.
      */}
      {ordersError && (
        <Alert
          severity="error"
          sx={{ mb: SP.card }}
          action={
            <Button color="inherit" size="small" onClick={onRefresh} disabled={isRefreshing}>
              Try again
            </Button>
          }
        >
          The filtered cost breakdown could not be loaded. Those tiles show
          &nbsp;&mdash;&nbsp; (unknown), not zero.
        </Alert>
      )}

      {/*
        🔴 W-41 — Konzept §2.2 F3: aus drei Streifen mit zwoelf Kacheln werden
        VIER Kacheln plus ein Disclosure. Die vier sind die, die man liest,
        ohne zu scrollen; die uebrigen acht sind nicht geloescht, sondern
        liegen unter "Cost breakdown" — dieselbe Zahl, eine Ebene tiefer.
        Konzept §2.2 F8: "Top carriers" ist eine Liste, keine Kennzahl, und
        steht deshalb nicht mehr im Streifen, sondern im Disclosure.
      */}
      {/* 🔴 C3-12 (G13a-4a): der Umschalter steht NICHT mehr in der Kachel und
          auch nicht in dieser Reihe — er ist ein RAHMEN-SEGMENT und liegt in
          der Schale (`FulfillmentDashboard`) UEBER der KPI-Reihe. Zwei Gruende,
          beide am Kode gemessen: eine Kennzahl-Kachel traegt keine Bedienung
          (OE-1/N3.5), und `messeP29` klickt den ersten Knopf INNERHALB von
          `liste-kpi` und verlangt, dass die KPI-Texte sich dabei nicht
          aendern — ein Segment darin wuerde genau das tun. */}
      <KpiRow sx={{ mb: SP.card }}>
        <KpiCell>
          <StatTile
            label="Overall cost"
            value={unknown(overallError, eur(totalCost))}
            loading={kpiLoading}
            hint={hintUnknown(
              overallError,
              overallAllKinds ? 'All kinds (DN + RT + IN + storage)' : 'Shipments only (DN + RT)'
            )}
            reserveHint
            labelTooltip="Sum of fulfillment net cost. Use the toggle above to include storage, inbound, and transfer references."
          />
        </KpiCell>

        <KpiCell>
          <FulfillmentCostKpiCard
            kind="DN"
            data={rollingKpis?.dn}
            loading={rollingLoading}
            error={rollingError}
            onRetry={onRefresh}
          />
        </KpiCell>

        <KpiCell>
          <FulfillmentCostKpiCard
            kind="RT"
            data={rollingKpis?.rt}
            loading={rollingLoading}
            error={rollingError}
            onRetry={onRefresh}
          />
        </KpiCell>

        <KpiCell>
          <StatTile
            label="Return rate"
            value={unknown(overallError, pctAt(lastReturnRate))}
            loading={kpiLoading}
            hint={hintUnknown(
              overallError,
              `RT ÷ DN${monthSuffix(lastReturnRate)}`
            )}
            reserveHint
            labelTooltip="RT count ÷ DN count per month. The value is the last month of the trend."
          />
        </KpiCell>
      </KpiRow>

      <CollapsibleSection title="Cost breakdown" defaultExpanded={false}>
        <KpiRow>
          <KpiCell>
            <StatTile
              label="Filtered total"
              value={unknown(ordersError, eur(kpiFiltered?.total))}
              loading={kpiLoading}
              hint={hintUnknown(
                ordersError,
                `Warehouse ${eur(kpiFiltered?.warehouseTotal)} • Transport ${eur(kpiFiltered?.transportTotal)}`
              )}
              reserveHint
              reserveSeries
              series={filteredSparkTotal}
              labelTooltip="Sum of DN + RT cost components (warehouse + transport) for the current selection."
            />
          </KpiCell>
          <KpiCell>
            {/* 🔴 W-41: diese Kachel bekam `kpiFiltered.sparklineShipments` —
                also die DN-ANZAHL je Monat — unter einem Kostenlabel. Sie
                zeichnet jetzt den Warehouskosten-Anteil je Monat, aus
                denselben Zeilen gerechnet wie der Wert darueber. */}
            <StatTile
              label="Warehouse trend"
              value={unknown(ordersError, eur(kpiFiltered?.warehouseTotal))}
              loading={kpiLoading}
              series={filteredSparkWarehouse}
              reserveSeries
              labelTooltip="Warehouse cost buckets (picking, packing, loading, materials, fees, returns, storage, other) per month for the current selection."
            />
          </KpiCell>
          <KpiCell>
            {/* 🔴 W-41: und diese bekam `sparklineReturnRate` — eine Quote in
                Prozent — unter einem Kostenlabel. */}
            <StatTile
              label="Transport trend"
              value={unknown(ordersError, eur(kpiFiltered?.transportTotal))}
              loading={kpiLoading}
              series={filteredSparkTransport}
              reserveSeries
              labelTooltip="Transport (shipping) cost per month for the current selection."
            />
          </KpiCell>
          <KpiCell>
            <StatTile
              label="Shipments (DN)"
              value={unknown(overallError, formatInt(kpiOverall?.shipmentsAll))}
              loading={kpiLoading}
              hint={hintUnknown(overallError, `Returns ${eur(kpiOverall?.kindTotals?.RT || 0)}`)}
              reserveHint
              labelTooltip="Distinct DN references counted in the dataset (secondary line shows RT totals)."
            />
          </KpiCell>
        </KpiRow>

        <KpiRow sx={{ mt: SP.card }}>
          <KpiCell>
            <StatTile
              label="Distinct countries"
              value={unknown(overallError, formatInt(kpiOverall?.distinctCountries))}
              loading={kpiLoading}
              hint={hintUnknown(overallError, 'Rolling window')}
              reserveHint
              labelTooltip="Unique order destination countries across the available shipment history."
            />
          </KpiCell>
          <KpiCell>
            <StatTile
              label={`All kinds${monthSuffix(lastAllKinds)}`}
              value={unknown(overallError, moneyAt(lastAllKinds))}
              loading={kpiLoading}
              series={sparkAll}
              reserveSeries
              labelTooltip="Monthly total of all fulfillment kinds (DN + RT + IN + storage). The value is the last month of the trend."
            />
          </KpiCell>
          <KpiCell>
            <StatTile
              label={`Shipments cost${monthSuffix(lastShipments)}`}
              value={unknown(overallError, moneyAt(lastShipments))}
              loading={kpiLoading}
              series={sparkShipments}
              reserveSeries
              labelTooltip="Monthly sum of DN and RT fulfillment costs. The value is the last month of the trend."
            />
          </KpiCell>
          <KpiCell>
            <Tooltip title="Number of DN shipments fulfilled per carrier.">
              <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                Top carriers
              </Typography>
            </Tooltip>
            <Stack spacing={SP.chip} sx={{ mt: SP.chip }}>
              {topCarriers.length
                ? topCarriers.map(([carrier, count]) => (
                    <Typography key={carrier} variant="body2">
                      {carrier}: {formatInt(count)}
                    </Typography>
                  ))
                : (
                  <Typography variant="body2" color="text.secondary">
                    {topCarriersNote}
                  </Typography>
                  )}
            </Stack>
          </KpiCell>
        </KpiRow>
      </CollapsibleSection>

      <Divider sx={{ mt: SP.card }} />
    </Box>
  );
}
